import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button, Input } from '../components/ui';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`/api/auth/reset-password/${token}`, { password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const fieldErrors = err.response?.data?.errors;
      setError(fieldErrors?.length ? fieldErrors.join(' ') : (err.response?.data?.msg || 'Something went wrong.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-neutral-50 to-neutral-100 p-6">
      <div className="w-full max-w-md">
        <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-[0_18px_50px_-20px_rgba(15,23,42,0.28)] p-8 sm:p-9">
          {done ? (
            <div className="text-center">
              <div className="w-12 h-12 rounded-xl bg-success-50 text-success-600 flex items-center justify-center mb-4 ring-1 ring-success-100 mx-auto">
                <CheckCircle2 size={22} />
              </div>
              <h2 className="text-xl font-bold text-neutral-900 tracking-tight">Password reset</h2>
              <p className="text-sm text-neutral-500 mt-2">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <div className="mb-7">
                <div className="w-12 h-12 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center mb-4 ring-1 ring-primary-100">
                  <Lock size={20} />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 tracking-tight">Set a new password</h2>
                <p className="text-sm text-neutral-500 mt-1.5">Choose a strong password for your account.</p>
              </div>

              {error && (
                <div className="mb-5 p-3 rounded-lg bg-danger-50 border border-danger-100 text-danger-700 text-sm flex items-start gap-2">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <Input
                  label="New password"
                  type={showPassword ? 'text' : 'password'}
                  leftIcon={<Lock size={16} />}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="text-neutral-400 hover:text-neutral-700 transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  required
                />
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={submitting}
                  rightIcon={!submitting && <ArrowRight size={18} />}
                >
                  {submitting ? 'Resetting…' : 'Reset password'}
                </Button>
              </form>
            </>
          )}
        </div>

        {!done && (
          <p className="text-sm text-neutral-500 text-center mt-6">
            <Link to="/login" className="text-primary-600 font-medium hover:text-primary-700">Back to sign in</Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
